<template>
  <block
    component-name="block-grid-project"
    body-class="grid grid-cols-12 gap-x-ui gap-y-4xl laptop:gap-y-0"
  >
    <template v-slot:default="blockData">
      <!-- GALLERY -->
      <header
        v-if="$attrs.data.body.description?.text"
        class="flex laptop:hidden flex-col col-span-full w-full gap-lg"
      >
        <txt-heading
          size="big"
          :overline="$t.labels.description"
          class="w-full"
        />
        <wrapper-reveal reveal="fade-from-bottom">
          <div
            v-html="$attrs.data.body.description.text"
            class="text-body-small text-skin-muted"
          ></div>
        </wrapper-reveal>
      </header>

      <section
        class="grid grid-cols-2 grid-flow-dense items-center w-full col-span-full laptop:row-start-1 laptop:row-end-2 laptop:col-start-4 laptop:col-end-12 gap-x-ui gap-y-4xl"
      >
        <card-media
          v-for="(item, index) in $attrs.data.body.items"
          :asset="{
            ...item.asset,
            sizes: {
              base: item.asset.aspectRatio === '4 / 5' ? 300 : 600,
              phablet: item.asset.aspectRatio === '4 / 5' ? 500 : 1000,
              tablet: item.asset.aspectRatio === '4 / 5' ? 650 : 1250,
              laptop: item.asset.aspectRatio === '4 / 5' ? 500 : 950,
              desktop: item.asset.aspectRatio === '4 / 5' ? 600 : 1200,
              extdesktop: item.asset.aspectRatio === '4 / 5' ? 800 : 1600,
            },
            loading:
              index > 5 || blockData?.loading === 'lazy' ? 'lazy' : 'eager',
          }"
          :use-favourite="{ img_id: item.asset.wp_id }"
          :use-moodboard="{ img_id: item.asset.wp_id }"
          class="w-full"
          :class="{
            'col-span-1': item.asset.aspectRatio === '4 / 5',
            'col-span-2': item.asset.aspectRatio !== '4 / 5',
          }"
        />
      </section>
      <!-- end GALLERY -->

      <!-- DESCRIPTION + COLLECTIONS -->
      <aside
        class="w-full col-span-full laptop:row-start-1 laptop:row-end-2 laptop:col-start-1 laptop:col-end-3"
      >
        <header
          v-if="$attrs.data.body.description"
          class="flex flex-col w-full gap-2xl"
        >
          <div
            v-if="$attrs.data.body.description.text"
            class="hidden laptop:flex flex-col w-full gap-lg"
          >
            <txt-heading
              size="big"
              :overline="$t.labels.description"
              class="w-full"
            />
            <wrapper-reveal reveal="fade-from-bottom">
              <div
                v-html="$attrs.data.body.description.text"
                class="text-body-small text-skin-muted"
              ></div>
            </wrapper-reveal>
          </div>

          <div
            v-if="
              $attrs.data.body.description.country ||
              $attrs.data.body.description.year ||
              $attrs.data.body.description.applications ||
              $attrs.data.body.description.architect ||
              $attrs.data.body.description.environments
            "
            class="flex flex-col"
          >
            <txt-heading
              size="big"
              :overline="$t.labels.details"
              class="w-full"
            />

            <ul class="flex flex-col gap-lg list-none py-lg">
              <li
                v-if="$attrs.data.body.description.country"
                class="flex flex-col w-full gap-2xs"
              >
                <wrapper-reveal reveal="fade-from-right">
                  <div
                    v-html="$t.labels.country"
                    class="text-overline-small"
                  ></div>
                </wrapper-reveal>

                <wrapper-reveal reveal="fade-from-bottom">
                  <div
                    v-html="
                      geolistStore.getCountryLabelByISO(
                        $attrs.data.body.description.country
                      )
                    "
                    class="text-body-small text-skin-muted"
                  ></div>
                </wrapper-reveal>
              </li>

              <li
                v-if="$attrs.data.body.description.year"
                class="flex flex-col w-full gap-2xs"
              >
                <wrapper-reveal reveal="fade-from-right">
                  <div
                    v-html="$t.labels.year"
                    class="text-overline-small"
                  ></div>
                </wrapper-reveal>

                <wrapper-reveal reveal="fade-from-bottom">
                  <div
                    v-html="$attrs.data.body.description.year"
                    class="text-body-small text-skin-muted"
                  ></div>
                </wrapper-reveal>
              </li>

              <li
                v-if="$attrs.data.body.description.applications"
                class="flex flex-col w-full gap-2xs"
              >
                <wrapper-reveal reveal="fade-from-right">
                  <div
                    v-html="$t.labels.attr_pa_applicazioni"
                    class="text-overline-small"
                  ></div>
                </wrapper-reveal>

                <wrapper-reveal reveal="fade-from-bottom">
                  <div
                    v-html="$attrs.data.body.description.applications"
                    class="text-body-small text-skin-muted"
                  ></div>
                </wrapper-reveal>
              </li>

              <li
                v-if="$attrs.data.body.description.architect"
                class="flex flex-col w-full gap-2xs"
              >
                <wrapper-reveal reveal="fade-from-right">
                  <div
                    v-html="$t.labels.architect"
                    class="text-overline-small"
                  ></div>
                </wrapper-reveal>

                <wrapper-reveal reveal="fade-from-bottom">
                  <div
                    v-html="$attrs.data.body.description.architect"
                    class="text-body-small text-skin-muted"
                  ></div>
                </wrapper-reveal>
              </li>

              <li
                v-if="$attrs.data.body.description.environments"
                class="flex flex-col w-full gap-2xs"
              >
                <wrapper-reveal reveal="fade-from-right">
                  <div
                    v-html="$t.labels.spaces"
                    class="text-overline-small"
                  ></div>
                </wrapper-reveal>

                <wrapper-reveal reveal="fade-from-bottom">
                  <div
                    v-html="$attrs.data.body.description.environments"
                    class="text-body-small text-skin-muted"
                  ></div>
                </wrapper-reveal>
              </li>
            </ul>
          </div>
        </header>

        <div
          v-if="$attrs.data.body.collections?.length"
          class="relative top-0 left-0 flex flex-col laptop:sticky laptop:top-40 gap-lg"
          :class="{ 'mt-xl': $attrs.data.body.description }"
        >
          <txt-heading
            size="big"
            :overline="$t.labels.related_collections"
            class="w-full"
          />

          <div
            class="flex flex-col phablet:flex-row laptop:flex-col items-stretch gap-y-2xl gap-x-ui w-full"
          >
            <card-collection
              v-for="(item, index) in $attrs.data.body.collections"
              variant="small"
              class="w-full"
              :title="item.title"
              :atelier="item.atelier || undefined"
              :resource="item.resource || undefined"
              :action="item.action"
              :asset="{
                ...item.asset,
                sizes: {
                  base: 600,
                  phablet: 950,
                  tablet: 1200,
                  laptop: 350,
                  desktop: 450,
                  extdesktop: 600,
                },
              }"
            />
          </div>
        </div>
      </aside>
      <!-- end DESCRIPTION + COLLECTIONS -->
    </template>
  </block>
</template>

<script setup>
import { useGeolistStore } from "@/stores/geolist";
const geolistStore = useGeolistStore();

const cardVariantSizes = ref({
  base: 600,
  phablet: 950,
  tablet: 1200,
  laptop: 350,
  desktop: 450,
  extdesktop: 600,
});
</script>
